/* What a remediation plan means on screen, decided once and in one place.
 *
 * Four judgements live here rather than inline in a component, because all four are the kind a
 * screen gets subtly and dangerously wrong:
 *
 * * **A tier is not a colour.** `toneOf` maps it to a semantic tone and the component maps the tone
 *   to a token, so a screen cannot decide that C looks like A today. Every tone also carries a
 *   *word* — WCAG 1.4.1, and this codebase has shipped a colour-alone status before.
 * * **What the server will accept.** `canSubmit`, `canDecide`, `canEmit` mirror the service's
 *   guards exactly. A button offered where the API refuses is how a user learns not to trust the
 *   screen — and on this screen the refusals are the product, not an inconvenience.
 * * **Flagged is not fixed.** `unfixableCount` is surfaced deliberately: "we found 40 problems and
 *   can correct 3" is the honest headline, and a fix-shaped UI hides the denominator.
 * * **Unsimulated is not zero.** `rowsReading` reports "not simulated" rather than `0`, because a
 *   confident zero would let an approver sign over counts nobody has taken.
 */

import { isSystemActor } from '@/features/audit';
import type {
  FixTier,
  Plan,
  PlanState,
  Operation,
  Proposal,
  RemediationMode,
} from '@/shared/api/generated/remediation';

export type TierTone = 'automatic' | 'review' | 'flagged';

/* Tier to tone. Deliberately not a pass-through: A and B are both "may be automatic in principle"
 * and read the same on screen, while C and D differ in a way a user must not miss — C is a fix
 * waiting for a human, D is not a fix at all. */
const TONE_BY_TIER: Readonly<Record<FixTier, TierTone>> = {
  A: 'automatic',
  B: 'automatic',
  C: 'review',
  D: 'flagged',
};

export function toneOf(proposal: Proposal): TierTone {
  return TONE_BY_TIER[proposal.tier];
}

/** Whether this proposal could ever reach data. Tier D never can. */
export function isApplicable(proposal: Proposal): boolean {
  return proposal.tier !== 'D';
}

/** Findings with no derivable correction — reported, never applied. */
export function unfixableCount(plan: Plan): number {
  return plan.proposals.filter((proposal) => !isApplicable(proposal)).length;
}

/** The Tier D findings themselves, for the panel that names each one and why. */
export function flaggedOnly(plan: Plan): readonly Proposal[] {
  return plan.proposals.filter((proposal) => !isApplicable(proposal));
}

const TERMINAL: readonly PlanState[] = [
  'rejected',
  'verified',
  'rolled_back',
  'abandoned',
] as const;

export function isTerminal(plan: Plan): boolean {
  return TERMINAL.includes(plan.state);
}

/** Simulation is refused on a terminal plan and pointless with nothing applicable. */
export function canSimulate(plan: Plan): boolean {
  return !isTerminal(plan) && plan.applicable_count > 0;
}

/* Only from `simulated`, mirroring `RemediationPlan.submit_for_approval`.
 *
 * The guard exists on the server because an approver looking at a drafted plan sees intentions with
 * no row counts. Repeating it here is not duplication for its own sake — a Submit button on a
 * drafted plan teaches a user that this screen guesses. */
export function canSubmit(plan: Plan): boolean {
  return (
    plan.state === 'simulated' && plan.mode !== 'advise' && plan.applicable_count > 0
  );
}

export function canDecide(plan: Plan): boolean {
  /** **Both of the server's guards, not just the state one.**
   *
   * A plan awaiting approval is decidable *by somebody*; `decision_refusal` is the server saying
   * whether that somebody is this reader. Checking only the state offered Approve to the person who
   * raised the request — who typed a reason, pressed it, and got a 403 explaining that raising a
   * request disqualifies you from approving it. Correct, and a thing to be told before typing.
   *
   * The refusal cannot be worked out here. The requester is recorded on the *approval request*, not
   * on the plan, and `created_by` is `system:remediation-planner` on anything the planner drafted —
   * so a comparison against it would offer Approve to exactly the wrong person. The delegation
   * chain, the second of the three refusals, is not published at all.
   *
   * This file's own docstring says these functions "mirror the service's guards exactly". That was
   * true of one guard out of two. */
  return plan.decision_standing === 'may_sign';
}

export function canEmit(plan: Plan): boolean {
  return plan.state === 'approved' && plan.mode !== 'advise';
}

export function hasArtifact(plan: Plan): boolean {
  return plan.state === 'emitted' || plan.state === 'executed';
}

/** Abandon closes a plan nobody will act on, and is refused once a plan is terminal. */
export function canAbandon(plan: Plan): boolean {
  return !isTerminal(plan);
}

/* Ordering for the list. Plans needing a human first, then everything else newest-first.
 *
 * `awaiting_approval` leads because it is the only state where somebody else is blocked waiting for
 * this screen; `approved` follows because the work is authorised and not yet done. */
const URGENCY: Readonly<Record<PlanState, number>> = {
  awaiting_approval: 0,
  approved: 1,
  simulated: 2,
  drafted: 3,
  emitted: 4,
  executed: 5,
  verified: 6,
  rolled_back: 7,
  rejected: 8,
  abandoned: 9,
};

export function orderForQueue(plans: readonly Plan[]): Plan[] {
  return [...plans].sort((left, right) => {
    const byUrgency = URGENCY[left.state] - URGENCY[right.state];
    if (byUrgency !== 0) return byUrgency;
    return right.created_at.localeCompare(left.created_at);
  });
}

export function stateLabelKey(state: PlanState): string {
  return `remediation.state.${state}`;
}

export function tierLabelKey(tier: FixTier): string {
  return `remediation.tier.${tier}`;
}

/* ---------------------------------------------------------------- the queue's shape */

export type StateTone = 'ok' | 'pending' | 'muted' | 'draft' | 'bad';

/* State to tone, and the two that matter are the two a careless map collapses.
 *
 * **`emitted` and `executed` are not terminal**, so neither takes the settled `ok` tone: an emitted
 * script still has to be run, and an executed plan is waiting to be verified or rolled back.
 * `verified` is the only genuine success. `rolled_back` is muted rather than red — an undo that
 * worked is not a failure — while `rejected` is, because somebody declined it.
 */
const STATE_TONE: Readonly<Record<PlanState, StateTone>> = {
  drafted: 'draft',
  simulated: 'draft',
  awaiting_approval: 'pending',
  approved: 'pending',
  emitted: 'pending',
  executed: 'pending',
  verified: 'ok',
  rejected: 'bad',
  rolled_back: 'muted',
  abandoned: 'muted',
};

export function stateToneOf(state: PlanState): StateTone {
  return STATE_TONE[state];
}

export type StateFilter = 'all' | 'waiting' | 'open' | 'closed';

export const STATE_FILTERS: readonly StateFilter[] = ['all', 'waiting', 'open', 'closed'];

/* Grouped rather than one button per state, because ten buttons is not a filter row and the
 * question a reader actually has is "what needs me". `waiting` is exactly the two states where a
 * person is the blocker. */
const WAITING: readonly PlanState[] = ['awaiting_approval', 'approved'] as const;

export function matchesState(plan: Plan, filter: StateFilter): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'waiting':
      return WAITING.includes(plan.state);
    case 'open':
      return !isTerminal(plan);
    case 'closed':
      return isTerminal(plan);
  }
}

export type ModeFilter = 'all' | RemediationMode;

export const MODE_FILTERS: readonly ModeFilter[] = ['all', 'advise', 'emit', 'execute'];

export function matchesMode(plan: Plan, filter: ModeFilter): boolean {
  return filter === 'all' || plan.mode === filter;
}

/** How many plans are blocked on a person rather than on a machine. */
export function waitingOnAPerson(plans: readonly Plan[]): number {
  return plans.filter((plan) => WAITING.includes(plan.state)).length;
}

/** The table this plan is about, or `null` when the plan does not say.
 *
 * **Read from the plan's own proposals rather than looked up in the catalogue**, and that is the
 * substantive choice rather than a saving. A plan is a statement about one schema state, so the
 * name it should carry is the one that was true when it was made — resolving `dataset_id` against
 * the live catalogue would title a plan with a name the plan may not be about any more, on the one
 * screen whose whole subject is what a script is going to change.
 *
 * Every proposal in a plan targets the same dataset, because a plan is keyed on one
 * `dataset_version_id`. The set is collapsed rather than the first taken anyway: if two ever
 * disagreed, showing one of them would be a quiet lie, and falling back to the id is the honest
 * answer to a plan that cannot say what it is about.
 *
 * `null` also covers the empty string the violation source coalesces to when the dataset row has
 * since been deleted — a plan about a table nobody can name any more, which the screen reports as
 * such rather than rendering `.` or a blank heading.
 */
export function relationOf(plan: Plan): string | null {
  const named = [
    ...new Set(
      plan.proposals
        .map((proposal) => proposal.qualified_name)
        .filter((name) => name !== ''),
    ),
  ];
  /* Destructured rather than indexed, so the single-element case is narrowed by the compiler
     instead of asserted with a `!` that would survive the array becoming empty. */
  const [only] = named;
  return named.length === 1 && only !== undefined ? only : null;
}

/** How much of a plan's id is enough to tell two rows apart. Not an identifier — see `identityOf`. */
export const SHORT_ID_CHARS = 8;

export interface PlanIdentity {
  /** The table, or `null` when the plan no longer names one. */
  readonly relation: string | null;
  /** An ISO instant, formatted by the component through `asInstant`. */
  readonly madeAt: string;
  readonly shortId: string;
  readonly id: string;
}

/** What tells two plans about the same table apart, given that nothing in the payload is a name.
 *
 * **`created_at` is the disambiguator, and it is the only field that always is.** Many plans are
 * made against one dataset — three rows reading `PUBLIC.FACT_SALES` is the ordinary case, not a
 * corner one — so the relation names the *subject* and never the row. A plan is a proposal made at
 * a moment, and two proposals about one table are two moments.
 *
 * The two candidates that lose are worth naming, because both look adequate. `state` does not
 * separate two drafted plans on one table, which is exactly the case that needs separating, and
 * the queue already carries it in a column of its own. The columns the proposals name describe a
 * plan without distinguishing it: two plans can trim the same column a week apart, and a cell
 * listing five columns is longer than the name it sits under.
 *
 * **Derived, and rendered as derived.** The instant appears under a verb saying where it came
 * from rather than as a title, because nothing here is a human-authored name and a line that reads
 * like one sends a reader looking for a field the API does not have.
 *
 * `shortId` is a tiebreaker beneath the instant and is deliberately **not** offered as the plan's
 * identifier. The listing is capped at 100 rows by the route's own `MAX_PLANS`, and eight hex
 * characters are one of 2^32, so the chance that any two ids on one screen share a prefix is
 * around one in a million — implausible enough to render. What that buys is a cell whose first two
 * lines are readable; what it costs is copy-and-paste of the full id from the table, which is why
 * the whole id stays on the cell's `title` and on the detail page, where a support conversation
 * gets it from.
 */
export function identityOf(plan: Plan): PlanIdentity {
  return {
    relation: relationOf(plan),
    madeAt: plan.created_at,
    shortId: plan.id.slice(0, SHORT_ID_CHARS),
    id: plan.id,
  };
}

export const SIMULATION_POLL_MS = 2_000;
/** How often an unsimulated plan is re-read while a measurement is in flight.

 * Two seconds because a steward is looking at the screen: a `count(*)` over a customer's table
 * usually answers in well under that. */

export const SIMULATION_POLL_LIMIT = 60;
/** How many polls before it gives up — two minutes.
 *
 * A simulation that has not answered by then is not slow, it is a worker that died or a source that
 * is unreachable. The plan is re-requestable, so the honest thing is to stop and let the reader
 * press it again rather than keep asking on their behalf for the life of the tab. */

export function simulationPollDelay(
  plan: Plan | undefined,
  polls: number,
): number | false {
  /** How long until this plan should be re-read, or `false` to stop.

   * **`/simulate` answers 202 and returns the plan unchanged** — the measurement happens in a
   * worker — so the single cache invalidation on success re-read a plan nothing had touched yet,
   * and the page then sat there. Its own comment said "a user watching for row counts needs the
   * next fetch"; nothing produced one, and the omission was invisible because the invalidation is
   * correct and merely too early.
   *
   * **Keyed on the plan's own state, not on whether this tab pressed the button.** `drafted` with
   * no `simulated_at` is the state a simulation moves *out* of, so a plan sitting in it is either
   * waiting for a measurement or waiting for a person, and the two are indistinguishable from here.
   * Polling both is the cheap side of that: one GET of one row, bounded, on a plan somebody has
   * open. Tracking "did I request one" locally would be exact and would miss the case that matters
   * most — a colleague pressed Simulate, or the tab was reopened mid-measurement. */
  if (plan?.simulated_at !== null || plan.state !== 'drafted') {
    return false;
  }
  return polls < SIMULATION_POLL_LIMIT ? SIMULATION_POLL_MS : false;
}

/* ---------------------------------------------------------------- counts that are not numbers */

export type Reading =
  | { readonly state: 'unsimulated' }
  | { readonly state: 'measured'; readonly rows: number };

/** Rows this plan would change, or the fact that nobody has measured.
 *
 * **`simulated_at === null` is the discriminator, never `estimated_rows === 0`.** A plan that ran a
 * simulation and found nothing to change reports a real zero, and that is a different fact from a
 * plan nobody has simulated — collapsing them would put a confident zero in front of an approver
 * signing for a write to a customer's production data.
 */
export function rowsReading(plan: Plan): Reading {
  if (plan.simulated_at === null) return { state: 'unsimulated' };
  return { state: 'measured', rows: plan.estimated_rows };
}

/** The same distinction per proposal, where `simulated_rows` is nullable on the wire. */
export function proposalRows(proposal: Proposal): Reading {
  if (proposal.simulated_rows === null) return { state: 'unsimulated' };
  return { state: 'measured', rows: proposal.simulated_rows };
}

/* ---------------------------------------------------------------- what may be done */

export type PlanAction =
  'simulate' | 'submit' | 'approve' | 'reject' | 'emit' | 'abandon';

export interface Offered {
  readonly action: PlanAction;
  /** The filled control — the step this plan is actually waiting for. */
  readonly primary: boolean;
}

/** The actions this plan's *state* admits, before any permission is consulted.
 *
 * Two separate questions kept separate: the state decides what is possible, `can()` decides what
 * this caller may be offered. Folding them would make an unpermitted control indistinguishable from
 * an impossible one.
 *
 * **There is no `execute`, on any plan, in any state.** No write adapter exists in this platform, so
 * an Execute button could only ever fail on click — and its absence is the honest answer. That is a
 * property of the deployment rather than of a plan, which is why the queue states it once at the
 * top rather than as a blocked chip on every row.
 */
export function offeredFor(plan: Plan): readonly Offered[] {
  const offered: Offered[] = [];
  if (canSimulate(plan)) {
    offered.push({ action: 'simulate', primary: plan.state === 'drafted' });
  }
  if (canSubmit(plan)) offered.push({ action: 'submit', primary: true });
  if (canDecide(plan)) {
    offered.push({ action: 'approve', primary: true });
    offered.push({ action: 'reject', primary: false });
  }
  if (canEmit(plan)) offered.push({ action: 'emit', primary: true });
  if (canAbandon(plan)) offered.push({ action: 'abandon', primary: false });
  return offered;
}

/** What is deliberately not offered here, and why — a chip carrying the reason on hover.
 *
 * Named rather than absent: a missing Emit on an unapproved plan reads as an oversight, where "an
 * emitted script is what somebody runs against production, so it needs the approval first" is the
 * platform explaining itself.
 */
export interface DecisionNotice {
  /** Which of the two headings to use. `signed` is a receipt; `refused` is a refusal. */
  readonly kind: 'signed' | 'refused';
  /** The server's own sentence, rendered verbatim. */
  readonly detail: string;
}

export function decisionNotice(plan: Plan): DecisionNotice | null {
  /** What to tell this reader about signing, or nothing when there is nothing to say.

   * **The two are not the same message, and shipping them as one was the defect.** A reader who has
   * signed is shown a heading saying they cannot sign this one — their own successful approval,
   * presented as a rejection. Somebody hit that and asked why they were being turned away; they
   * were not, they were the first of two.
   *
   * `may_sign` and `not_open` produce nothing: the first because the buttons are right there, the
   * second because nobody may sign a plan that is not awaiting a decision, which is not a statement
   * about this reader. */
  switch (plan.decision_standing) {
    case 'already_signed':
      return plan.decision_detail === null
        ? null
        : { kind: 'signed', detail: plan.decision_detail };
    case 'refused':
      return plan.decision_detail === null
        ? null
        : { kind: 'refused', detail: plan.decision_detail };
    case 'may_sign':
    case 'not_open':
      return null;
  }
}

export function blockedFor(plan: Plan): readonly string[] {
  const blocked: string[] = [];
  if (!canEmit(plan) && plan.mode !== 'advise' && !isTerminal(plan)) {
    blocked.push('remediation.blocked.emit');
  }
  if (plan.mode === 'advise' && !isTerminal(plan)) {
    blocked.push('remediation.blocked.advise');
  }
  return blocked;
}

/* ---------------------------------------------------------------- the plan's own record */

export interface Fact {
  readonly key: string;
  /** What to render. A copy key when `translate` is set, otherwise literal text. */
  readonly value: string;
  readonly mono: boolean;
  /** The whole of what `value` abbreviates, rendered as a `title`.
   *
   * **Strictly an expansion**, asserted by a test: `value` is always a prefix of it. It is not a
   * general hover slot — the version fact tried to use it for the underlying id beside an ordinal,
   * which is a different fact rather than a longer form of the same one, and one field meaning both
   * is how a reader comes to hover expecting one and get the other. */
  readonly expands?: string;
  /** Where this fact points, when it points somewhere. */
  readonly to?: string;
  /** `value` is a copy key rather than data — a label, or a sentence standing in for a gap. */
  readonly translate?: boolean;
  /** Rendered quietly: the value is a stand-in rather than a fact about this plan. */
  readonly muted?: boolean;
}

export interface FactContext {
  /** The schema version's ordinal, resolved from the catalogue by the caller.
   *
   * `null` while that read is in flight or if it fails, which is a real state and not the same as
   * "version 0" — the panel falls back to the id rather than inventing a number. */
  readonly versionNumber: number | null;
  /** Where this plan's originating run is read, built by the caller so the model states no route
   * of its own — a presentation model that knew the router would be a second place routes live. */
  readonly executionHref: string;
}

/** The identifying facts, in the order a reader checks them.
 *
 * **Six of the seven were raw UUIDs**, which is what a reader met first on this screen: four
 * thirty-six-character identifiers stacked, none of which names anything. The table this plan is
 * about — the single thing somebody opening it wants — was not among them at all, because
 * `dataset_id` is not a name.
 *
 * Every value here is still derived from the payload or from a read the caller made. Nothing is
 * invented: where a readable form is unavailable the id stays, marked as a fallback, rather than a
 * placeholder that reads like data.
 *
 * `dataset_version_id` is here and is not decoration: **a plan is a statement about one schema
 * state**, so a plan built against columns since dropped must not read as current. Keying it on the
 * version is what makes that visible rather than something a reader has to know — and an ordinal is
 * what makes it legible, since "v3" is a thing a person can compare and a UUID is not.
 */
export function factsOf(plan: Plan, context: FactContext): readonly Fact[] {
  const relation = relationOf(plan);
  return [
    /* First, because it is the question a reader arrives with. `dataset_id` is gone from the panel
       rather than demoted: it names nothing a person can act on, and the id that identifies this
       plan for a support conversation is the plan's own, below. */
    relation === null
      ? {
          key: 'remediation.fact.table',
          value: 'remediation.unnamedRelation',
          mono: false,
          translate: true,
          muted: true,
        }
      : { key: 'remediation.fact.table', value: relation, mono: true },
    context.versionNumber === null
      ? {
          /* The read has not answered, or the version is gone from the catalogue. The id is the
             honest fallback — it is what the plan actually stores — and it is muted so it does not
             read as the answer. */
          key: 'remediation.fact.version',
          value: shortId(plan.dataset_version_id),
          mono: true,
          expands: plan.dataset_version_id,
          muted: true,
        }
      : {
          /* The id is deliberately not carried alongside. "v3" is the answer to which schema state
             this plan was built against; the UUID behind it is machinery a reader cannot act on,
             and the schema history screen is where it belongs. It comes back in the fallback above,
             where it is the only answer available. */
          key: 'remediation.fact.version',
          value: `v${String(context.versionNumber)}`,
          mono: false,
        },
    {
      key: 'remediation.fact.mode',
      value: `remediation.mode.${plan.mode}`,
      mono: false,
      translate: true,
    },
    {
      /* A link, because "which run found this" is a question with an answer one click away, and the
         id was previously a value a reader had to paste into a URL by hand. */
      key: 'remediation.fact.execution',
      value: shortId(plan.execution_id),
      mono: true,
      expands: plan.execution_id,
      to: context.executionHref,
    },
    isSystemActor(plan.created_by)
      ? {
          /* `system:remediation-planner` is accurate and reads as a person's username. Naming it as
             automatic is the same distinction the audit screen draws, through the same predicate —
             a second rule for what counts as a system actor is how the two come to disagree. */
          key: 'remediation.fact.createdBy',
          value: 'remediation.byThePlanner',
          mono: false,
          translate: true,
        }
      : { key: 'remediation.fact.createdBy', value: plan.created_by, mono: false },
    /* Last, and still an identifier: this is what a support conversation and every API call for
       this plan are keyed on, so the full value is one hover away rather than gone. */
    {
      key: 'remediation.fact.plan',
      value: shortId(plan.id),
      mono: true,
      expands: plan.id,
    },
    plan.approval_request_id === null
      ? {
          key: 'remediation.fact.approval',
          value: 'remediation.noApproval',
          mono: false,
          translate: true,
          muted: true,
        }
      : {
          key: 'remediation.fact.approval',
          value: shortId(plan.approval_request_id),
          mono: true,
          expands: plan.approval_request_id,
        },
  ];
}

function shortId(id: string): string {
  /** The first `SHORT_ID_CHARS` of an identifier, for a cell where the whole one is noise.
   *
   * Shared with the queue's row identity rather than a second constant: two abbreviations of the
   * same id at different lengths would make a reader comparing a row against a panel do arithmetic.
   * The whole value travels beside it in `expands` — shortening on screen is not the same as
   * discarding, and the id is what a support conversation needs. */
  return id.slice(0, SHORT_ID_CHARS);
}

export interface LifecycleEntry {
  /** An ISO instant, formatted by the component. */
  readonly when: string;
  /** A copy key for the verb. */
  readonly verbKey: string;
  readonly who: string | null;
}

/** The plan's history, **derived from the plan's own timestamps and from nothing else**.
 *
 * There are four recorded instants — created, simulated, decided, and whatever the current state
 * implies — and no route exposes a per-plan event log. So this lists exactly what the record holds.
 * Emission and execution carry no timestamp on the plan, which is why neither appears here even
 * when the state says they happened: inventing an instant, or borrowing `decided_at`, would put a
 * time against an event nobody recorded. The audit chain is where the full history lives.
 */
export function lifecycleOf(plan: Plan): readonly LifecycleEntry[] {
  const entries: LifecycleEntry[] = [
    {
      when: plan.created_at,
      verbKey: 'remediation.life.created',
      who: plan.created_by,
    },
  ];
  if (plan.simulated_at !== null) {
    entries.push({
      when: plan.simulated_at,
      verbKey: 'remediation.life.simulated',
      who: null,
    });
  }
  if (plan.signatures.length > 0) {
    /* **One line per signature, because two people signed and the plan named one.** The plan's own
       `decided_by` is written by the signature that *satisfied* the request, so a history derived
       from it showed the second approver of a two-eyes plan and lost the first — half an N-eyes
       story. Each decision carries its own verb rather than reading the plan's state, because a
       first approval on a plan that was later rejected is still an approval somebody gave. */
    for (const signature of plan.signatures) {
      entries.push({
        when: signature.decided_at,
        verbKey: signature.approved
          ? 'remediation.life.approved'
          : 'remediation.life.rejected',
        who: signature.approver,
      });
    }
  } else if (plan.decided_at !== null) {
    /* The pre-signatures fallback, kept because a stored decision must never vanish from the
       history if the request read fails or an old payload lacks the list. Which decision it was
       comes from the state, because `decided_at` is written by both — a rejected plan showing
       "approved" would be the worst possible caption on this screen. */
    entries.push({
      when: plan.decided_at,
      verbKey:
        plan.state === 'rejected'
          ? 'remediation.life.rejected'
          : 'remediation.life.approved',
      who: plan.decided_by,
    });
  }
  return entries;
}

/* ------------------------------------------------------------- what a signature would unlock */

export interface SigningLine {
  /** The proposal's own id — a real key for a rendered list, so no position arithmetic. */
  readonly id: string;
  /** The ICU key for one proposal's plain-language sentence. */
  readonly key: string;
  readonly args: Readonly<Record<string, string>>;
  /** The measured count, or `null` for a proposal nobody has simulated. */
  readonly rows: number | null;
}

/* One sentence per operation, total over the enum with `satisfies` so a new operation is a compile
 * error in the file whose subject is "an approver must know what this does" — the silent branch a
 * `default:` would give it is precisely the sentence that must not be missing. */
const SIGNING_KEY = {
  trim: 'remediation.does.trim',
  uppercase: 'remediation.does.uppercase',
  lowercase: 'remediation.does.lowercase',
  normalise_whitespace: 'remediation.does.normalise_whitespace',
  reference_map: 'remediation.does.reference_map',
  copy_from_source: 'remediation.does.copy_from_source',
  recompute: 'remediation.does.recompute',
} satisfies Record<Operation, string>;

/* The mode's consequence, total for the same reason. `advise` is unreachable here — `canSubmit`
 * refuses it, so an advise plan never awaits approval — and mapped anyway: totality is what keeps
 * that a fact about today's guard rather than a hole waiting for the guard to change. */
const MODE_CONSEQUENCE_KEY = {
  advise: 'remediation.does.mode.advise',
  emit: 'remediation.does.mode.emit',
  execute: 'remediation.does.mode.execute',
} satisfies Record<RemediationMode, string>;

export function modeConsequenceKey(plan: Plan): string {
  return MODE_CONSEQUENCE_KEY[plan.mode];
}

/** Each applicable proposal as a sentence an approver can sign without reading SQL.

 * **The approval screen showed a state machine and asked for a signature.** What the plan would
 * actually do lived in a proposals table of tiers and operation names, and in the emitted SQL —
 * which does not exist yet, because emission is what the signature unlocks. So the person whose
 * whole role is "deliberately look" was given the least readable form at the moment it mattered.
 *
 * Derived entirely from the plan: the operation names the transformation, the proposal names the
 * column and table, `simulated_rows` is the count taken on a read-only connection. Nothing is
 * summarised away — one line per applicable proposal, because "and 3 more" is not a thing to sign.
 * Tier D findings are deliberately absent here and counted by the caller through `unfixableCount`:
 * they are what the plan does NOT do, which is its own sentence rather than a quieter line.
 */
export function signingLines(plan: Plan): readonly SigningLine[] {
  return plan.proposals.flatMap((proposal) => {
    /* Bound locally so the null check narrows — a `filter` + cast would say the same thing while
       teaching the type checker nothing. */
    const action = proposal.action;
    if (action === null) return [];
    return [
      {
        id: proposal.id,
        key: SIGNING_KEY[action.operation],
        args: {
          column: proposal.column,
          relation: proposal.qualified_name,
        },
        rows: proposal.simulated_rows,
      },
    ];
  });
}

/* ---------------------------------------------------------------- instants a person can read */

const DAY_AND_TIME = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/** A raw ISO instant rendered for a person, falling back to the original when it will not parse.
 *
 * Falling back rather than throwing: a malformed timestamp is a server problem, and blanking a
 * lifecycle row over it would hide the event as well as the fault.
 */
export function asInstant(value: string): string {
  const at = new Date(value);
  return Number.isNaN(at.getTime()) ? value : DAY_AND_TIME.format(at);
}
